import { paths } from "@/api-schema";
import { getInitials } from "@/common/helpers/get-initials";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

type SuccessResponse =
  | paths["/v1/azure/application/{azureClientUuid}/icon"]["get"]["responses"][200]["content"]["application/json"]
  | null;

export interface Props {
  uuid: string;
  name: string;
}
export const AzureApplicationAvatar = ({ uuid, name }: Props) => {
  const { t } = useTranslation("common");
  const { data, isPending } = useQuery<SuccessResponse, AxiosError>({
    queryKey: ["applications", uuid, "info", "logoUrl"],
    queryFn: async () => {
      const response = await alphartisApiAxios.get<SuccessResponse>(`/v1/azure/application/${uuid}/icon`, {
        validateStatus: (status) => status === 200 || status === 404,
      });

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    },
    retry: (failureCount, error) => {
      if (error.response?.status === 404) {
        return false;
      }

      return failureCount < 1;
    },
  });

  return (
    <Avatar>
      {data ? (
        <AvatarImage src={data} alt={name ? t("ImageOf", { name }) : t("UserImage")} />
      ) : (
        <>
          {isPending && (
            <AvatarFallback>
              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
            </AvatarFallback>
          )}
          {!isPending && <AvatarFallback>{name ? getInitials(name) : "?"}</AvatarFallback>}
        </>
      )}
    </Avatar>
  );
};
